import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * Reusable contact form for category pages (legs, bénévole, partenaire).
 * Uses the existing /api/contact endpoint with a `category` field.
 */
const CategoryContactForm = ({ category, t, defaultSubject, testid = "category-contact-form" }) => {
  const [form, setForm] = useState({ name: "", email: "", subject: defaultSubject || "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", { ...form, category });
      setDone(true);
      toast.success(t.success);
      setForm({ name: "", email: "", subject: defaultSubject || "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.error);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white rounded-2xl p-10 md:p-12 border border-black/5 text-center" data-testid={`${testid}-success`}>
        <CheckCircle2 size={56} className="text-emerald-500 mx-auto mb-4" />
        <h3 className="font-serif text-2xl text-[#002B49] mb-2 font-semibold">{t.success}</h3>
        <p className="text-[#64748B]">{t.successSubtitle}</p>
        <button onClick={() => setDone(false)} className="btn-outline-dark mt-6 py-2.5 px-5 text-sm">
          {t.sendAnother}
        </button>
      </div>
    );
  }

  return (
    <motion.form
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      onSubmit={onSubmit}
      className="bg-white rounded-2xl p-6 md:p-10 shadow-[0_20px_60px_-30px_rgba(0,43,73,0.2)] border border-black/5"
      data-testid={testid}
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t.name}>
          <input type="text" required value={form.name} onChange={update("name")} className="form-input" data-testid={`${testid}-name`} />
        </Field>
        <Field label={t.email}>
          <input type="email" required value={form.email} onChange={update("email")} className="form-input" data-testid={`${testid}-email`} />
        </Field>
      </div>
      <Field label={t.subject} className="mt-4">
        <input type="text" required value={form.subject} onChange={update("subject")} className="form-input" data-testid={`${testid}-subject`} />
      </Field>
      <Field label={t.message} className="mt-4">
        <textarea required rows="5" value={form.message} onChange={update("message")} className="form-input resize-none" data-testid={`${testid}-message`} />
      </Field>
      <button type="submit" disabled={submitting} className="btn-primary mt-6 w-full sm:w-auto justify-center disabled:opacity-60" data-testid={`${testid}-submit`}>
        {submitting ? "..." : t.send} <Send size={16} className="ml-2" />
      </button>

      <style>{`
        .form-input { width:100%; margin-top:.5rem; padding:.75rem 1rem; border-radius:.5rem; border:1px solid #E5E7EB; outline:none; transition:border-color .2s, box-shadow .2s; font-family:'Manrope',sans-serif; font-size:.95rem; color:#0A192F; background:#fff; }
        .form-input:focus { border-color:#002B49; box-shadow:0 0 0 3px rgba(0,43,73,.1); }
      `}</style>
    </motion.form>
  );
};

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-xs font-medium text-[#64748B] uppercase tracking-wider">{label} <span className="text-[#C5A059]">*</span></span>
    {children}
  </label>
);

export default CategoryContactForm;
